import classNames from 'classnames';
import { CKButton } from 'clearkit';
import { useRef, useState } from 'react';

import { useAccountUpdateBillingInfoMutation } from '~/generated/graphql';

import { ButtonContainer } from '../unified/ButtonContainer';
import { useStatusToast } from '../unified/hooks';
import { SettingsCard } from '../unified/SettingsCard';
import StripeCCInput, { Ref as StripeCCInputRef } from './StripeCCInput';

export type CardDetails = {
  cardLastFour: string;
  cardType: string;
};

type StripeFormProps = {
  stripePublishableKey: string;
  handleToggleEdit: () => void;
  handleSave: (value: CardDetails) => void;
  cardElementClass?: string;
  cardErrorsClass?: string;
  discardButtonClass?: string;
  saveButtonClass?: string;
};

export function StripeForm({
  stripePublishableKey,
  handleToggleEdit,
  handleSave,
  cardElementClass,
  cardErrorsClass,
  discardButtonClass,
  saveButtonClass,
}: StripeFormProps) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { addSuccessToast } = useStatusToast();
  const stripeCCInputRef = useRef<StripeCCInputRef>(null);

  const [updateBillingInfo] = useAccountUpdateBillingInfoMutation({
    onCompleted: () => {
      addSuccessToast({
        description: 'Your settings have been updated.',
        heading: 'Billing Details Updated',
      });
    },
  });

  const onSubmit = () => {
    const { stripe, card } = stripeCCInputRef.current || {};

    if (stripe && card) {
      stripe?.createToken(card).then(async (result) => {
        if (result.error) {
          setErrorMessage(result?.error?.message as string);
        } else {
          updateBillingInfo({
            variables: {
              input: {
                cardToken: result.token.id,
              },
            },
          });
          handleSave({
            cardLastFour: result.token.card?.last4 as string,
            cardType: result.token.card?.brand as string,
          });
        }
      });
    }
  };

  return (
    <>
      <StripeCCInput
        className={classNames(
          'p-2 border border-gray-200 rounded',
          cardElementClass,
        )}
        ref={stripeCCInputRef}
        stripePublishableKey={stripePublishableKey}
      />
      <div className={cardErrorsClass} id="card-errors" role="alert">
        {errorMessage}
      </div>
      <SettingsCard.Footer className="-mx-8 !pb-0">
        <ButtonContainer>
          <CKButton
            className={discardButtonClass}
            onClick={handleToggleEdit}
            variant="simple"
          >
            Discard changes
          </CKButton>

          <CKButton
            className={saveButtonClass}
            onClick={onSubmit}
            variant="bold"
            variantColor="green"
          >
            Save changes
          </CKButton>
        </ButtonContainer>
      </SettingsCard.Footer>
    </>
  );
}
